import { Form } from '../components/ui/validated-form';
import SendFeedbackDialog from "../components/SendFeedbackDialog";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicDataCheckAPI } from "../lib/api";
import { Input } from "../components/ui/input";
import { dataCheckBirthdateError, latestDataCheckBirthdate } from "../lib/dataCheckBirthdate";

const emptyForm = {
  first_name: "", last_name: "", birthdate: "", address: "", postal_code: "",
  city: "", country: "Deutschland", email: "", phone: "", website: "",
};

export default function PublicDataCheck() {
  const { token } = useParams();
  const [practice, setPractice] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);
  const [error, setError] = useState("");
  const [birthdateError, setBirthdateError] = useState("");

  useEffect(() => {
    let live = true;
    publicDataCheckAPI.get(token)
      .then((response) => { if (live) setPractice(response.data); })
      .catch((requestError) => { if (live) setError(requestError?.response?.data?.detail || "Dieser Datencheck-Link ist nicht verfügbar."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token]);

  const change = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (name === "birthdate") setBirthdateError(dataCheckBirthdateError(value));
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const dateError = dataCheckBirthdateError(form.birthdate);
    setBirthdateError(dateError);
    if (dateError) return;
    setSubmitting(true);
    setError("");
    try {
      await publicDataCheckAPI.submit(token, { ...form, privacy_acknowledged: privacyAcknowledged });
      setDone(true);
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || "Die Angaben konnten nicht gesendet werden. Bitte versuche es erneut.";
      if (typeof message === "string" && message.includes("Geburtsdatum")) setBirthdateError(message);
      else setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return <main className="min-h-screen bg-[#F5F5F7] px-4 py-10 text-[#1D1D1F]">
    <div className="mx-auto max-w-2xl rounded-3xl border border-[#E5E5EA] bg-white p-6 shadow-sm sm:p-10">
      <p className="text-sm text-[#86868B]">{practice?.practice_name || "Patientendaten"}</p>
      <h1 className="mt-2 text-3xl font-semibold">Patientendaten prüfen</h1>
      {loading ? <p className="mt-6">Datencheck wird geladen …</p> : done ? <div className="mt-6 rounded-xl bg-green-50 p-5 text-green-900">
        Vielen Dank. Deine Angaben wurden an die Praxis gesendet und werden dort geprüft. Es wurde kein Termin gebucht.
      </div> : practice ? <>
        <p className="mt-3 text-sm leading-6 text-[#636366]">Trage deine aktuellen Kontaktdaten ein. Die Praxis prüft die Angaben, bevor sie eine Patientenakte aktualisiert oder neu anlegt. Hier wird kein Termin gebucht.</p>
        <Form onSubmit={submit} className="mt-7 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium">Vorname *<input name="first_name" value={form.first_name} onChange={change} required maxLength={100} autoComplete="given-name" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
            <label className="block text-sm font-medium">Nachname *<input name="last_name" value={form.last_name} onChange={change} required maxLength={100} autoComplete="family-name" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="birthdate" className="block text-sm font-medium">Geburtsdatum <span className="font-normal text-[#86868B]">(hilft beim Abgleich)</span></label>
              <Input id="birthdate" name="birthdate" type="date" value={form.birthdate} onChange={change}
                max={latestDataCheckBirthdate()} aria-invalid={Boolean(birthdateError)}
                aria-describedby={birthdateError ? "birthdate-error" : undefined}
                className={`mt-2 h-12 rounded-xl px-4 ${birthdateError ? "border-red-500 bg-red-50 focus:ring-red-500" : ""}`} />
              {birthdateError && <p id="birthdate-error" role="alert" className="mt-2 text-sm text-red-600">{birthdateError}</p>}
            </div>
            <label className="block text-sm font-medium">Telefon<input name="phone" type="tel" value={form.phone} onChange={change} maxLength={40} autoComplete="tel" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          </div>
          <label className="block text-sm font-medium">E-Mail<input name="email" type="email" value={form.email} onChange={change} maxLength={254} autoComplete="email" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          <label className="block text-sm font-medium">Straße und Hausnummer<input name="address" value={form.address} onChange={change} maxLength={250} autoComplete="street-address" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium">Postleitzahl<input name="postal_code" value={form.postal_code} onChange={change} maxLength={20} autoComplete="postal-code" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
            <label className="block text-sm font-medium">Ort<input name="city" value={form.city} onChange={change} maxLength={100} autoComplete="address-level2" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          </div>
          <label className="block text-sm font-medium">Land<input name="country" value={form.country} onChange={change} maxLength={100} autoComplete="country-name" className="mt-2 w-full rounded-xl border px-4 py-3" /></label>
          <label className="sr-only" aria-hidden="true">Website<input name="website" value={form.website} onChange={change} tabIndex={-1} autoComplete="off" /></label>
          <p className="text-xs leading-5 text-[#86868B]">Die Angaben werden zur Prüfung und Pflege deiner Patientendaten an die Praxis übermittelt. Es erfolgt keine automatische Zusammenführung nur anhand deines Namens.</p>
          {practice.privacy_text && <div className="max-h-36 overflow-y-auto whitespace-pre-wrap rounded-xl bg-[#F5F5F7] p-3 text-xs text-[#636366]">{practice.privacy_text}</div>}
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" required checked={privacyAcknowledged} onChange={(event) => setPrivacyAcknowledged(event.target.checked)} className="mt-1" /><span>Ich habe die Hinweise zur Datenverarbeitung zur Kenntnis genommen. <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline">Plattform-Datenschutz</a></span></label>
          {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-800">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full rounded-xl bg-black px-5 py-3 font-medium text-white disabled:opacity-50">{submitting ? "Wird gesendet …" : "Angaben an Praxis senden"}</button>
        </Form>
      </> : <p role="alert" className="mt-6 rounded-xl bg-red-50 p-4 text-red-800">{error}</p>}
    </div>
      <SendFeedbackDialog state={submitting ? "sending" : "idle"} channel="Formular" />
  </main>;
}
